import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface WorkDNAProfile {
  id: string;
  person_id: string;
  person_type: string;
  person_name?: string;
  person_email?: string;
  person_title?: string;
  dimensions: {
    drive: number;
    influence: number;
    steadiness: number;
    precision: number;
    autonomy: number;
    collaboration: number;
    adaptability: number;
    resilience: number;
  };
  overall_confidence: number;
  communication_guide: {
    preferred_pace: string;
    detail_level: string;
    interaction_style: string;
    decision_approach: string;
    tips: string[];
  };
  privacy_level: string;
  visible_to: string;
  created_at: string;
  updated_at: string;
}

const DIMENSION_LABELS: Record<string, { label: string; color: string }> = {
  drive: { label: 'Drive', color: 'bg-red-500' },
  influence: { label: 'Influence', color: 'bg-orange-500' },
  steadiness: { label: 'Steadiness', color: 'bg-green-500' },
  precision: { label: 'Precision', color: 'bg-blue-500' },
  autonomy: { label: 'Autonomy', color: 'bg-purple-500' },
  collaboration: { label: 'Collaboration', color: 'bg-teal-500' },
  adaptability: { label: 'Adaptability', color: 'bg-yellow-500' },
  resilience: { label: 'Resilience', color: 'bg-indigo-500' },
};

function DimensionBar({ dimension, value }: { dimension: string; value: number }) {
  const config = DIMENSION_LABELS[dimension] ?? { label: dimension, color: 'bg-gray-500' };
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground w-28 shrink-0">{config.label}</span>
      <div className="flex-1 bg-muted rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all ${config.color}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-sm font-medium w-8 text-right">{value}</span>
    </div>
  );
}

export function WorkDNA() {
  const [personType, setPersonType] = useState<'staff' | 'contact'>('staff');
  const [selectedProfile, setSelectedProfile] = useState<WorkDNAProfile | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['workdna-profiles', personType],
    queryFn: () =>
      api<{ data: WorkDNAProfile[] }>(`workdna/profiles?person_type=${personType}&limit=50`),
  });

  const profiles = data?.data ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">WorkDNA</h1>
        <p className="text-muted-foreground">
          Behavioral profiling for better communication, team dynamics, and client relationships.
        </p>
      </div>

      {/* Person type tabs */}
      <div className="flex gap-2 mb-6">
        {(['staff', 'contact'] as const).map((type) => (
          <button
            key={type}
            onClick={() => { setPersonType(type); setSelectedProfile(null); }}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${
              personType === type
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {type === 'staff' ? 'Staff' : 'Contacts'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profiles list */}
        <div className="lg:col-span-1">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : profiles.length === 0 ? (
            <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
              No {personType} profiles yet. Run assessments to generate WorkDNA profiles.
            </div>
          ) : (
            <div className="space-y-2">
              {profiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => setSelectedProfile(profile)}
                  className={`w-full text-left rounded-lg border p-4 transition-colors ${
                    selectedProfile?.id === profile.id
                      ? 'border-primary bg-primary/5'
                      : 'bg-card hover:border-primary/30'
                  }`}
                >
                  <div className="font-medium text-sm">
                    {profile.person_name ?? profile.person_id}
                  </div>
                  {profile.person_title && (
                    <div className="text-xs text-muted-foreground">{profile.person_title}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    Confidence: {Math.round(profile.overall_confidence * 100)}%
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Profile detail */}
        <div className="lg:col-span-2">
          {selectedProfile ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold">
                      {selectedProfile.person_name ?? selectedProfile.person_id}
                    </h2>
                    {selectedProfile.person_title && (
                      <p className="text-sm text-muted-foreground">{selectedProfile.person_title}</p>
                    )}
                    {selectedProfile.person_email && (
                      <p className="text-xs text-muted-foreground">{selectedProfile.person_email}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    selectedProfile.privacy_level === 'full'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {selectedProfile.privacy_level}
                  </span>
                </div>

                {/* Dimensions */}
                <h3 className="text-sm font-medium mb-3">Behavioral Dimensions</h3>
                <div className="space-y-2.5">
                  {Object.entries(selectedProfile.dimensions).map(([key, value]) => (
                    <DimensionBar key={key} dimension={key} value={value} />
                  ))}
                </div>
              </div>

              {/* Communication Guide */}
              {selectedProfile.communication_guide && (
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="text-sm font-medium mb-4">Communication Guide</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-xs text-muted-foreground">Preferred Pace</span>
                      <p className="text-sm font-medium capitalize">{selectedProfile.communication_guide.preferred_pace}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Detail Level</span>
                      <p className="text-sm font-medium capitalize">{selectedProfile.communication_guide.detail_level}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Interaction Style</span>
                      <p className="text-sm font-medium capitalize">{selectedProfile.communication_guide.interaction_style.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Decision Approach</span>
                      <p className="text-sm font-medium capitalize">{selectedProfile.communication_guide.decision_approach}</p>
                    </div>
                  </div>
                  {selectedProfile.communication_guide.tips.length > 0 && (
                    <div>
                      <span className="text-xs text-muted-foreground block mb-2">Tips</span>
                      <ul className="space-y-1">
                        {selectedProfile.communication_guide.tips.map((tip, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-primary mt-0.5">-</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
              Select a profile to view WorkDNA details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
